<script>
export default {
  props: {
    // 父層 index.vue 抓到的 /admin/merchant/setting { type:'hotel' } 回傳 data
    hotelInfo: {
      type: Object,
      default: () => null,
    },
    // 父層抓到的 /admin/product/search 回傳（你說 productsInfo.items）
    productsInfo: {
      type: Object,
      default: () => null,
    },
    // 父層傳入：用 productId 找名稱（你上一題做的 function）
    getProductName: {
      type: Function,
      default: () => () => '（未知分店）',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      renderKey: 0,
      localBranches: [], // UI 用的 list
      submitting: false,
      hotelInfo:[],
    }
  },

  watch: {
    hotelInfo: {
      immediate: true,
      deep: true,
      handler() {
        this.buildBranches()
      },
    },
    productsInfo: {
      immediate: true,
      deep: true,
      handler() {
        this.buildBranches()
      },
    },
  },

  methods: {
    buildBranches() {
      const items = this.productsInfo?.items || []
      const setting = Array.isArray(this.hotelInfo?.productSetting) ? this.hotelInfo.productSetting : []

      // productSetting -> Map：productId => isEnable
      const settingMap = {}
      setting.forEach((s) => {
        if (!s?.productId) return
        settingMap[s.productId] = Number(s.isEnable) ? 1 : 0
      })

      // 以 productsInfo.items 為主（列表），再套上 productSetting 的開關值
      this.localBranches = items.map((p) => {
        const id = p.id
        return {
          productId: id,
          // 後端可能沒有 fullAddress/phone，沒有就顯示 '-'
          name: p.name || this.getProductName(id),
          fullAddress: p.fullAddress || '-',
          phone: p.phone || '-',
          isEnable: settingMap[id] ?? 0,
        }
      })

      // 🔧 如果後端 productSetting 裡有某些 productId 不在 items，也要補進來（你前面提到的需求）
      const existingIds = new Set(items.map((p) => p.id))
      setting.forEach((s) => {
        if (!s?.productId) return
        if (existingIds.has(s.productId)) return
        this.localBranches.push({
          productId: s.productId,
          name: this.getProductName(s.productId),
          fullAddress: '-',
          phone: '-',
          isEnable: Number(s.isEnable) ? 1 : 0,
        })
      })

      // 強制重繪（避免首次渲染資料晚到造成 UI 沒更新的狀況）
      this.renderKey += 1
    },

    buildPayload() {
      // 回存只需要 productSetting，其它欄位要保留原本的（交給父層合併最安全）
      const productSetting = this.localBranches.map((b) => ({
        productId: b.productId,
        isEnable: Number(b.isEnable) ? 1 : 0,
      }))

      const hotelInfo = this.hotelInfo
      hotelInfo.productSetting = productSetting ; 
      return hotelInfo ; 
    },

    async save() {
      // 不打 API，交給父層
      this.submitting = true
      try {
        const payload = this.buildPayload()
        this.$emit('save', payload)
      } finally {
        this.submitting = false
      }
    },

    cancel() {
      // 回復到父層最後一次給的資料
      this.buildBranches()
      this.$message?.info?.('取消編輯')
      this.$emit('cancel')
    },
  },
}
</script>

<template>
  <div class="outer">
    <div :class="$style.container" v-loading="loading">
      <div :class="$style.header">
        <h3>住宿分店設定</h3>
      </div>

      <div :class="$style.branchList" :key="renderKey">
        <div
          v-for="branch in localBranches"
          :key="branch.productId"
          :class="[$style.branchItem, { [$style.disabled]: !branch.isEnable }]"
        >
          <div :class="$style.info">
            <span :class="$style.name">{{ branch.name }}</span>
            <span :class="$style.fullAddress">{{ branch.fullAddress }}</span>
            <span :class="$style.phone">{{ branch.phone }}</span>
          </div>

          <div :class="$style.action">
            <span :class="$style.statusLabel">關閉</span>

            <el-switch
              v-model="branch.isEnable"
              :active-value="1"
              :inactive-value="0"
              active-color="#F37736"
              inactive-color="#EAEAEA"
            />

            <span :class="[$style.statusText, { [$style.active]: branch.isEnable }]"> 開啟 </span>
          </div>
        </div>
      </div>

      <div :class="$style.footer">
        <el-button class="is-round" @click="cancel">取消編輯</el-button>
        <el-button :loading="loading || submitting" class="is-round" type="primary" @click="save">
          儲存編輯
        </el-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.outer {
  overflow: scroll;
  width: 100%;
  padding: 18px 20px 0;
}
</style>

<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';

.container {
  background-color: #fff;
  padding: 20px;
}

.header {
  margin-bottom: 20px;
  h3 {
    font-size: 16px;
    font-weight: normal;
    color: #333;
  }
}

.branchList {
  margin-bottom: 40px;
}

.branchItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border: 1px solid #EAEAEA;
  border-radius: 8px;
  margin-bottom: 15px;
  background-color: #fff;

  &.disabled {
    background-color: #FAFAFA;
    color: #999;

    .name,
    .fullAddress,
    .phone {
      color: #ccc;
    }
  }
}

.info {
  display: grid;
  grid-template-columns: 100px 200px 1fr;
  align-items: center;
  gap: 40px;
}

.action {
  display: flex;
  align-items: center;
  gap: 10px;
}

.statusLabel {
  color: #999;
  font-size: 14px;
}

.statusText {
  font-size: 14px;
  color: #999;

  &.active {
    color: #F37736;
  }
}
</style>